import { createContext } from 'react';

// Ensure API URL always ends with /api.
// When the app is accessed from a domain different from REACT_APP_BACKEND_URL
// (e.g. custom domain box.skillupwithlevelup.com vs preview.emergentagent.com),
// use the current window origin so requests are same-origin and bypass
// Cloudflare cross-domain blocking.
export const getApiUrl = () => {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    
    if (host !== 'localhost' && host !== '127.0.0.1') {
      const configuredHost = envUrl
        ? (() => { try { return new URL(envUrl).hostname; } catch { return ''; } })()
        : '';
      
      // If the current page domain differs from the configured backend domain,
      // use the current origin so the request is same-origin (backend is co-hosted)
      if (configuredHost && configuredHost !== host) {
        return `${window.location.protocol}//${window.location.host}/api`;
      }
    }
  }

  const finalUrl = envUrl && !envUrl.endsWith('/api') ? envUrl + '/api' : (envUrl || '/api');
  return finalUrl;
};

export const API_URL = getApiUrl();

// Auth Context
export const AuthContext = createContext(null);

// Chunk size for large-file uploads: 10 MB safely passes any proxy limit
const CHUNK_SIZE = 10 * 1024 * 1024;

// API Helper
export const api = {
  token: localStorage.getItem('token'),

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  },

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
      ...options.headers
    };

    const url = `${API_URL}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'An error occurred' }));
        const err = new Error(errorData.detail || 'Request failed');
        err.status = response.status;
        throw err;
      }

      return response.json();
    } catch (err) {
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        throw new Error('Unable to connect to server. Please try again later.');
      }
      throw err;
    }
  },

  /**
   * Upload a file via FormData.
   * - Files ≤ 10 MB: sent as a single multipart request (fast, direct).
   * - Files > 10 MB: automatically split into 10 MB chunks and uploaded
   *   sequentially through /api/upload/chunk (bypasses proxy body-size limits,
   *   supports files up to 2 GB+).
   *
   * @param {string} endpoint - e.g. "/courses/{id}/files"
   * @param {FormData} formData - must contain 'file' (File) and 'file_type' (string)
   * @param {function|null} onProgress - called with 0–100 as upload progresses
   */
  async uploadFile(endpoint, formData, onProgress = null) {
    const file = formData.get('file');
    const fileType = formData.get('file_type');

    // Route large files through the chunked upload path
    if (file && file.size > CHUNK_SIZE) {
      const match = endpoint.match(/\/courses\/([^/]+)\/files/);
      if (match) {
        return this._uploadChunked(match[1], file, fileType, onProgress);
      }
    }

    // Direct upload for small files
    const headers = {
      ...(this.token && { 'Authorization': `Bearer ${this.token}` })
    };

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        if (response.status === 413) {
          // Proxy rejected the body — fall back to chunked upload automatically
          const match = endpoint.match(/\/courses\/([^/]+)\/files/);
          if (match && file) {
            console.warn('[Upload] 413 received — retrying via chunked upload');
            return this._uploadChunked(match[1], file, fileType, onProgress);
          }
          throw new Error('File is too large. Please try a smaller file or contact your administrator.');
        }
        const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(error.detail || 'Upload failed');
      }

      if (onProgress) onProgress(100);
      return response.json();

    } catch (err) {
      // Network-level failure (proxy dropped connection for large file) — try chunked
      if ((err.name === 'TypeError' || err.message === 'Failed to fetch') && file) {
        const match = endpoint.match(/\/courses\/([^/]+)\/files/);
        if (match) {
          console.warn('[Upload] Network failure — retrying via chunked upload');
          return this._uploadChunked(match[1], file, fileType, onProgress);
        }
        throw new Error('Upload failed: server did not respond. Check your connection and try again.');
      }
      throw err;
    }
  },

  /**
   * Internal: split a File into CHUNK_SIZE pieces and POST each piece to
   * /api/upload/chunk. The backend assembles them and writes to Azure Blob.
   */
  async _uploadChunked(courseId, file, fileType, onProgress = null) {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const uploadId = (crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

    console.log(`[Chunked Upload] Starting: ${file.name} (${file.size} bytes, ${totalChunks} chunks)`);

    const headers = {
      ...(this.token && { 'Authorization': `Bearer ${this.token}` })
    };

    let lastResult = null;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunkBlob = file.slice(start, end);

      const fd = new FormData();
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('original_name', file.name);
      fd.append('file_type', fileType);
      fd.append('course_id', courseId);
      fd.append('chunk', chunkBlob, file.name);

      console.log(`[Chunked Upload] Uploading chunk ${i + 1}/${totalChunks} (${chunkBlob.size} bytes)`);

      const response = await fetch(`${API_URL}/upload/chunk`, {
        method: 'POST',
        headers,
        body: fd
      });

      console.log(`[Chunked Upload] Chunk ${i + 1} response:`, response.status, response.statusText);

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ detail: 'Chunk upload failed' }));
        console.error(`[Chunked Upload] Failed on chunk ${i + 1}:`, errData);
        throw new Error(errData.detail || `Failed on chunk ${i + 1}/${totalChunks}`);
      }

      lastResult = await response.json();
      console.log(`[Chunked Upload] Chunk ${i + 1} complete:`, lastResult.status || 'OK');
      
      if (onProgress) {
        onProgress(Math.round(((i + 1) / totalChunks) * 100));
      }
    }

    console.log(`[Chunked Upload] All chunks uploaded successfully for ${file.name}`);

    // Guard: if the backend still returned "partial" after the last chunk (e.g. multi-pod
    // issue where an earlier chunk went to a different pod), surface a clear error instead
    // of silently showing 0 files.
    if (lastResult && lastResult.status === 'partial') {
      throw new Error(
        'Upload incomplete — the server could not assemble all chunks. ' +
        'Please try again. If this keeps happening, try uploading smaller files.'
      );
    }

    return lastResult;
  }
};
